import { ErrorNotice } from '@/components/ui';
import { useCreateProject } from '@/hooks/queries';
import { useEffect, useState, type FormEvent } from 'react';

/** Paleta de la barra lateral: el color distingue proyectos de un vistazo. */
const COLORS = ['#0f766e', '#147a5a', '#9a6410', '#a8348a', '#2f6dc0', '#7a43d6', '#b8433a'];

/**
 * Alta de proyecto. La clave (SYN, AUT...) es el prefijo de todas las tareas y
 * no se puede cambiar despues, porque las claves ya emitidas dejarian de
 * coincidir; por eso se propone a partir del nombre pero se deja editar.
 */
export function NewProjectDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (projectId: string) => void;
}) {
  const createProject = useCreateProject();

  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLORS[0]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Mientras no se escriba una clave a mano, se propone a partir del nombre.
  const effectiveKey = keyTouched ? key : suggestKey(name);
  const valid = name.trim().length >= 2 && /^[A-Z][A-Z0-9]{1,7}$/.test(effectiveKey);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!valid) {
      return;
    }
    createProject.mutate(
      {
        key: effectiveKey,
        name: name.trim(),
        description: description.trim() || undefined,
        color,
      },
      { onSuccess: (project) => onCreated(project.id) },
    );
  }

  return (
    <div
      onClick={onClose}
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(4,4,10,.62)] p-6"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-project-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[460px] overflow-hidden rounded-panel border border-line bg-surface shadow-[0_24px_60px_rgba(0,0,0,.6)]"
      >
        <header className="border-b border-line-soft bg-sunken px-5 py-4">
          <h2 id="new-project-title" className="font-display text-base font-semibold">
            Nuevo proyecto
          </h2>
          <p className="mt-0.5 text-[12.5px] text-subtle">
            Se crea con su canal de chat y contigo como Product Owner.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-5 py-5">
          <div>
            <label htmlFor="project-name" className="mb-1.5 block text-[12.5px] font-semibold">
              Nombre
            </label>
            <input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
              placeholder="Automatizaciones"
              className="w-full rounded-control border border-line-strong px-3 py-2.5 text-[13.5px] outline-none focus:border-brand"
            />
          </div>

          <div>
            <label htmlFor="project-key" className="mb-1.5 block text-[12.5px] font-semibold">
              Clave
            </label>
            <input
              id="project-key"
              value={effectiveKey}
              onChange={(e) => {
                setKeyTouched(true);
                setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8));
              }}
              required
              aria-describedby="project-key-hint"
              className="w-full rounded-control border border-line-strong px-3 py-2.5 font-mono text-[13.5px] uppercase outline-none focus:border-brand"
            />
            <p id="project-key-hint" className="mt-1.5 text-[11.5px] text-faint">
              Prefijo de las tareas: <span className="font-mono">{effectiveKey || 'CLAVE'}-1</span>,{' '}
              <span className="font-mono">{effectiveKey || 'CLAVE'}-2</span>… No se puede cambiar
              despues.
            </p>
          </div>

          <div>
            <label htmlFor="project-desc" className="mb-1.5 block text-[12.5px] font-semibold">
              Descripcion <span className="font-normal text-faint">(opcional)</span>
            </label>
            <textarea
              id="project-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full resize-none rounded-control border border-line-strong px-3 py-2.5 text-[13.5px] outline-none focus:border-brand"
            />
          </div>

          <fieldset>
            <legend className="mb-1.5 text-[12.5px] font-semibold">Color</legend>
            <div className="flex gap-2">
              {COLORS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setColor(option)}
                  aria-label={`Color ${option}`}
                  aria-pressed={color === option}
                  className={`size-7 rounded-full transition-transform ${
                    color === option ? 'scale-110 ring-2 ring-brand ring-offset-2 ring-offset-surface' : ''
                  }`}
                  style={{ background: option }}
                />
              ))}
            </div>
          </fieldset>

          {createProject.error && (
            <ErrorNotice>{(createProject.error as Error).message}</ErrorNotice>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-control px-3 py-2 text-[13px] font-medium text-subtle transition-colors hover:text-ink"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!valid || createProject.isPending}
              className="rounded-control bg-brand px-4 py-2 text-[13px] font-semibold text-brand-on transition-colors hover:bg-brand-strong disabled:opacity-50"
            >
              {createProject.isPending ? 'Creando…' : 'Crear proyecto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** "Portal de clientes" -> "PORTAL". Solo una propuesta, se puede editar. */
function suggestKey(name: string): string {
  const words = name
    .normalize('NFD')
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return '';
  }
  // Con varias palabras se usan sus iniciales; con una, sus primeras letras.
  const raw = words.length > 1 ? words.map((w) => w[0]).join('') : words[0];
  return raw.toUpperCase().slice(0, 8);
}
